/**
 * @voltxampere/ui — Catenary wire curve math (Sprint 52, ADR-0085).
 *
 * VXA imza özelliği. Kablo asılmış gibi sarkar.
 *
 * ## Math
 *
 * True catenary: `y = a · cosh(x / a)` — transcendental, slow per-frame
 * for the 1000-wire frame budget (Lighthouse perf gate). We approximate
 * with a cubic Bezier whose two control points each sit 1/3 of the way
 * along the chord and at depth `sag · 4/3` below the line — this matches
 * the chord-midpoint sag of a true catenary for slack values in the
 * documented range (1.00 → 1.30) within < 2 % visual error and renders
 * in a single Path2D `C` segment per wire.
 *
 * `slack` is the dimensionless ratio `arcLength / chordLength`, so:
 *   - `1.000` → taut wire, pixel-identical to a straight line
 *   - `1.050` → "subtle, professional" default sag (~12 px on a 100 px chord)
 *   - `1.300` → dramatic droop (~47 px on a 100 px chord)
 *
 * For an inextensible heavy chain anchored at equal heights, the
 * mid-chord vertical sag of a parabolic approximation is
 *   `sag = sqrt(3 · D² · (slack − 1) / 4)`
 * where `D` is the chord length. We use this closed form because the
 * full parabola arc-length formula would force a Newton solve per wire.
 *
 * ## Edge cases
 *
 * Three fall back to a straight `M … L …` path:
 *   1. `D < 20 px` — visually flat regardless of slack
 *   2. `|Δx| < 5 px` — vertical or near-vertical wires don't sag
 *      sideways; horizontal sag is meaningless
 *   3. `slack ≤ 1.001` — taut, no observable curve
 *
 * ## Why not a true catenary or a quadratic Bezier?
 *
 * - True `cosh` is transcendental; the 1000-wire frame budget (Sprint 52
 *   ADR-0085 perf benchmark) caps render at 16 ms.
 * - Quadratic Bezier has only one control point, so the curve is
 *   asymmetric whenever the two pins are at different heights — the sag
 *   leans toward the higher pin instead of hanging straight down.
 *
 * Cubic Bezier (this implementation) hangs symmetrically and is one
 * `Path2D` instruction.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
const STRAIGHT_FALLBACK_MIN_LENGTH = 20;
const STRAIGHT_FALLBACK_MIN_DX = 5;
const STRAIGHT_FALLBACK_SLACK = 1.001;
const BEZIER_HEIGHT_FACTOR = 4 / 3;
function chordLength(p1, p2) {
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    return Math.sqrt(dx * dx + dy * dy);
}
function shouldFallbackToStraight(params) {
    const D = chordLength(params.p1, params.p2);
    if (D < STRAIGHT_FALLBACK_MIN_LENGTH)
        return true;
    if (params.slack < STRAIGHT_FALLBACK_SLACK)
        return true;
    if (Math.abs(params.p2.x - params.p1.x) < STRAIGHT_FALLBACK_MIN_DX)
        return true;
    return false;
}
function straightPath(params, length) {
    const { p1, p2 } = params;
    return {
        d: `M ${String(p1.x)} ${String(p1.y)} L ${String(p2.x)} ${String(p2.y)}`,
        midpoint: { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 },
        sag: 0,
        length,
    };
}
/**
 * Computes the parabolic-Bezier approximation of a catenary between
 * two pins. Returns an SVG path string consumable by `Path2D` plus
 * geometry metadata for hit-testing, label placement, and bounding-
 * box computation.
 */
export function computeCatenary(params) {
    const { p1, p2, slack } = params;
    const D = chordLength(p1, p2);
    if (shouldFallbackToStraight(params)) {
        return straightPath(params, D);
    }
    const slackFactor = slack - 1;
    // Closed-form parabolic mid-chord sag. Documented at file-top.
    const sag = Math.sqrt((3 * D * D * slackFactor) / 4);
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    // Cubic Bezier control points each at chord-third with vertical
    // depth `sag · 4/3`. Symmetric across the chord midpoint.
    const sagDepth = sag * BEZIER_HEIGHT_FACTOR;
    const c1x = p1.x + dx / 3;
    const c1y = p1.y + dy / 3 + sagDepth;
    const c2x = p2.x - dx / 3;
    const c2y = p2.y - dy / 3 + sagDepth;
    const d = `M ${String(p1.x)} ${String(p1.y)} ` +
        `C ${String(c1x)} ${String(c1y)} ${String(c2x)} ${String(c2y)} ` +
        `${String(p2.x)} ${String(p2.y)}`;
    // Visual midpoint sits at the chord midpoint plus the sag depth.
    const midpoint = {
        x: (p1.x + p2.x) / 2,
        y: (p1.y + p2.y) / 2 + sag,
    };
    return {
        d,
        midpoint,
        sag,
        length: D * slack,
    };
}
/**
 * Discrete sample of points along the catenary, used for quadtree
 * hit-test indexing. Returns `count + 1` points (endpoints inclusive).
 *
 * Caller treats consecutive samples as line segments; `count = 16`
 * yields a worst-case sample-to-sample distance of ~`chord/16 · 1.5`
 * even for the deepest documented sag, which is comfortably below the
 * 4 px hit-test tolerance shipped by `hit-test.ts`.
 *
 * Falls back to a 2-point straight sample when `computeCatenary` would
 * fall back, so callers never need to special-case the straight path.
 */
export function sampleCatenary(params, count = 16) {
    if (count < 1) {
        return [params.p1, params.p2];
    }
    if (shouldFallbackToStraight(params)) {
        return [params.p1, params.p2];
    }
    const { p1, p2, slack } = params;
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const D = chordLength(p1, p2);
    const slackFactor = slack - 1;
    const sag = Math.sqrt((3 * D * D * slackFactor) / 4);
    const sagDepth = sag * BEZIER_HEIGHT_FACTOR;
    const c1x = p1.x + dx / 3;
    const c1y = p1.y + dy / 3 + sagDepth;
    const c2x = p2.x - dx / 3;
    const c2y = p2.y - dy / 3 + sagDepth;
    // De Casteljau-style cubic Bezier evaluation at uniform t.
    const samples = [];
    for (let i = 0; i <= count; i++) {
        const t = i / count;
        const u = 1 - t;
        const u2 = u * u;
        const u3 = u2 * u;
        const t2 = t * t;
        const t3 = t2 * t;
        const x = u3 * p1.x + 3 * u2 * t * c1x + 3 * u * t2 * c2x + t3 * p2.x;
        const y = u3 * p1.y + 3 * u2 * t * c1y + 3 * u * t2 * c2y + t3 * p2.y;
        samples.push({ x, y });
    }
    return samples;
}
//# sourceMappingURL=catenary-math.js.map